import { toast } from "react-toastify";
import moment from "moment";
import { createSlice } from "@reduxjs/toolkit";
import * as SchemaMonitorAPI from "api/SchemaMonitorAPI";
import { AibleClientFactory } from "client/aible/factory";
import * as foldersReducer from "containers/Folders/reducer";
import * as accountsReducer from "containers/Accounts/reducer";
import * as globalReducer from "containers/App/reducer";
import * as homeReducer from "containers/Home/reducer";
import * as setupReducer from "containers/Setup/reducer";
import * as errorsReducer from "containers/Errors/reducer";
import * as viewReducer from "containers/View/reducer";
import * as RelationUtils from "utils/relation";
import { isEnabled } from "utils/featureFlags";
import { getIsDemoAccount } from "utils/request";
import CSVIcon from "images/EDG/DB/CSVIcon.svg";
import OracleIcon from "images/EDG/DB/oracle.svg";
import AWSIcon from "images/EDG/DB/AWS.svg";
import AzureIcon from "images/EDG/DB/AZURE.svg";
import SnowflakeIcon from "images/EDG/DB/snowflake.svg";
import SalesforceIcon from "images/EDG/DB/salesforce.png";
import DatabricksIcon from "images/EDG/DB/databricks.png";
import RedshiftIcon from "images/EDG/DB/redshift.svg";
import BigQueryIcon from "images/bigquery.svg";
import PostgreSQLIcon from "images/EDG/DB/PostgreSQLIcon.svg";
import MSSQLIcon from "images/EDG/DB/mssql.svg";
import CSVIconGray from "images/csv_light_grey.svg";
import CSVIconWhite from "images/csv_white.svg";
import SalesForceIconGray from "images/EDG/DB/db-logos-gray/sfdc.svg";
import SalesForceIconWhite from "images/EDG/DB/db-logos-white/sfdc.svg";
import OracleIconGray from "images/EDG/DB/db-logos-gray/orcl.svg";
import OracleIconWhite from "images/EDG/DB/db-logos-white/orcl.svg";
import SnowflakeIconGray from "images/EDG/DB/db-logos-gray/snowflake.svg";
import SnowflakeIconWhite from "images/EDG/DB/db-logos-white/snowflake.svg";
import DatabricksIconGray from "images/EDG/DB/db-logos-gray/databricks.svg";
import DatabricksIconWhite from "images/EDG/DB/db-logos-white/databricks.svg";
import BigQueryIconGray from "images/EDG/DB/db-logos-gray/bigquery.svg";
import { filterUndefined } from "utils/collect";
import { getSchemaColumns } from "utils/expression";
export const UPDATE_FIELD = "schemaMonitor/UPDATE_FIELD";
export const UPDATE_SCHEMA_MONITOR = "schemaMonitor/UPDATE_SCHEMA_MONITOR";
export const UPDATE_SCHEMA_MONITOR_FIELD = "schemaMonitor/UPDATE_SCHEMA_MONITOR_FIELD";
export const DELETE_SCHEMA_MONITOR = "schemaMonitor/DELETE_SCHEMA_MONITOR";
export const RESET = "schemaMonitor/RESET";
export const INCREMENT_SCHEMA_MONITOR_UPLOAD_COUNT = "schemaMonitor/INCREMENT_SCHEMA_MONITOR_UPLOAD_COUNT";
export const REFRESH_MONITOR_WAIT_TIME = 60000;
// Constants for enabling dataset processing on the backend
const BACKEND_PROCESSING_ACCOUNTS = ["GCP"];
const BACKEND_PROCESSING_ACCOUNT_DATE = "2023-04-11T00:00:00";
export const DATA_SOURCES = [
    {
        value: "CSV",
        label: "CSV Upload",
        shortLabel: "CSV",
        image: CSVIcon,
        iconGray: CSVIconGray,
        iconWhite: CSVIconWhite,
        settings: [],
        hide: true
    },
    {
        value: "S3",
        label: "AWS Upload",
        shortLabel: "AWS",
        image: AWSIcon,
        settings: [],
        hide: true
    },
    {
        value: "AZUREBLOBSTORE",
        label: "Azure Upload",
        shortLabel: "Azure",
        image: AzureIcon,
        settings: [],
        hide: true
    },
    {
        value: "BIGQUERY",
        label: "BigQuery",
        image: BigQueryIcon,
        iconGray: BigQueryIconGray,
        iconWhite: BigQueryIcon,
        allowedAccounts: ["GCP"],
        settings: [
            {
                label: "Database Connection Name",
                value: "name"
            },
            { label: "Project Id", value: "project_id" },
            { label: "Dataset Id", value: "dataset_id" },
            // removed for now as it is not being used
            // {
            //   label: "Processing Project Id",
            //   value: "processing_project_id",
            //   isOptional: true
            // },
            {
                label: "Upload Credentials JSON",
                value: "credentials",
                isJsonUpload: true,
                isOptional: true
            }
        ]
    },
    {
        value: "SALESFORCE",
        label: "Salesforce",
        image: SalesforceIcon,
        iconGray: SalesForceIconGray,
        iconWhite: SalesForceIconWhite,
        settings: [
            {
                label: "Database Connection Name",
                value: "name"
            },
            { label: "User", value: "user" },
            { label: "Password", value: "password", isPassword: true },
            { label: "Instance", value: "instance" },
            { label: "Security Token", value: "securityToken", isPassword: true },
            { label: "Sandbox", value: "sandbox", isCheckbox: true, default: false }
        ]
    },
    {
        value: "ORACLE",
        label: "Oracle",
        image: OracleIcon,
        iconGray: OracleIconGray,
        iconWhite: OracleIconWhite,
        settings: [
            {
                label: "Database Connection Name",
                value: "name"
            },
            { label: "User", value: "user" },
            {
                label: "Password",
                value: "password",
                isPassword: true
            },
            { label: "Database", value: "database" },
            {
                label: "Host",
                value: "host"
            },
            { label: "Port", value: "port" },
            { label: "SID", value: "sid" }
        ]
    },
    {
        value: "SNOWFLAKE",
        label: "Snowflake",
        image: SnowflakeIcon,
        iconGray: SnowflakeIconGray,
        iconWhite: SnowflakeIconWhite,
        settings: [
            {
                label: "Database Connection Name",
                value: "name"
            },
            { label: "Account Identifier", value: "account" },
            {
                label: "Login With OAuth 2.0",
                value: "oAuthLogin",
                isCheckbox: true,
                default: false,
                dontSend: true,
                showCheckboxCloudbaseUrl: {
                    url: "/v1/schema_monitor/oauth2/redirect",
                    showCondition: true,
                    label: "OAuth2 Callback URL:",
                    labelStyle: {
                        fontWeight: "bold",
                        marginRight: "8px",
                        marginLeft: "10px"
                    },
                    urlStyle: {
                        padding: "2px 4px",
                        backgroundColor: "#EFECF6",
                        borderRadius: "3px",
                        cursor: "pointer"
                    }
                }
            },
            { label: "User", value: "user", hideFunc: (s) => s?.["oAuthLogin"] == true },
            { label: "Password", value: "password", isPassword: true, hideFunc: (s) => s?.["oAuthLogin"] == true },
            { label: "Client ID", value: "client_id", hideFunc: (s) => s?.["oAuthLogin"] != true },
            { label: "Client Secret", value: "client_secret", hideFunc: (s) => s?.["oAuthLogin"] != true },
            { label: "Custom Role", value: "role", hideFunc: (s) => s?.["oAuthLogin"] != true, dontSendNull: true },
            { label: "Database", value: "database" },
            { label: "Schema", value: "schema" },
            { label: "Warehouse", value: "warehouse" }
        ]
    },
    // {
    //   value: "TERADATA",
    //   label: "Teradata",
    //   image: TeradataIcon,
    //   iconGray: TeradataIconGray,
    //   iconWhite: TeradataIconWhite,
    //   settings: [
    //     {
    //       label: "Database Connection Name",
    //       value: "name"
    //     },
    //     { label: "User", value: "user" },
    //     { label: "Password", value: "password", isPassword: true },
    //     { label: "Database", value: "database" },
    //     { label: "Host", value: "host" },
    //     { label: "Port", value: "port" }
    //   ]
    // },
    {
        value: "SIMBASPARK",
        label: "Databricks (JDBC)",
        hide: true,
        image: DatabricksIcon,
        iconGray: DatabricksIconGray,
        iconWhite: DatabricksIconWhite,
        settings: [
            {
                label: "Database Connection Name",
                value: "name"
            },
            { label: "Access Token", value: "password", isPassword: true },
            { label: "Database", value: "database" },
            { label: "Host", value: "host" },
            { label: "HTTP Path", value: "httpPath" },
            {
                label: "Active Directory",
                value: "activeDirectory",
                isCheckbox: true,
                default: false
            }
        ]
    },
    {
        value: "DATABRICKS",
        label: "Databricks",
        image: DatabricksIcon,
        iconGray: DatabricksIconGray,
        iconWhite: DatabricksIconWhite,
        settings: [
            {
                label: "Database Connection Name",
                value: "name"
            },
            { label: "Host", value: "host" },
            { label: "HTTP Path", value: "http_path" },
            { label: "Access Token", value: "access_token", isPassword: true },
            { label: "Catalog", value: "catalog" },
            { label: "Schema", value: "schema" }
        ]
    },
    {
        value: "REDSHIFT",
        label: "Redshift",
        image: RedshiftIcon,
        //iconGray: DatabricksIconGray,
        //iconWhite: DatabricksIconWhite,
        allowedAccounts: ["AWS"],
        settings: [
            {
                label: "Database Connection Name",
                value: "name"
            },
            { label: "Username", value: "user" },
            { label: "Password", value: "password", isPassword: true },
            { label: "Database", value: "database" },
            { label: "Schema", value: "schema" },
            { label: "Host", value: "host" },
            { label: "Port", value: "port" },
            { label: "Region", value: "region", isOptional: true },
            {
                label: "Cluster Identifier",
                value: "cluster_identifier",
                isOptional: true
            },
            {
                label: "Identity Provider",
                value: "credentials_provider",
                default: "None",
                allowedValues: [
                    { value: "None", label: "None" },
                    {
                        value: "AzureCredentialsProvider",
                        label: "Azure Active Directory"
                    }
                ]
            },
            {
                label: "IdP Tenant",
                value: "idp_tenant",
                hideFunc: (s) => s?.["credentials_provider"] != "AzureCredentialsProvider"
            },
            {
                label: "Login URL",
                value: "login_url",
                hideFunc: (s) => s?.["credentials_provider"] != "AzureCredentialsProvider"
            },
            {
                label: "Client Id",
                value: "client_id",
                hideFunc: (s) => s?.["credentials_provider"] != "AzureCredentialsProvider"
            },
            {
                label: "Client Secret",
                value: "client_secret",
                isPassword: true,
                hideFunc: (s) => s?.["credentials_provider"] != "AzureCredentialsProvider"
            }
        ]
    },
    // {
    //   value: "mysql",
    //   label: "MySQL",
    //   image: MySQLIcon,
    //   settings: [
    //     { label: "Database Connection Name", value: "name", default: "New Database Connection" },
    //     { label: "Account", value: "account" },
    //     { label: "User", value: "user" },
    //     { label: "Password", value: "password", isPassword: true },
    //     { label: "Database", value: "database" },
    //     { label: "Table", value: "table" },
    //   ],
    // },
    {
        value: "POSTGRESQL",
        label: "PostgreSQL",
        image: PostgreSQLIcon,
        settings: [
            { label: "Database Connection Name", value: "name", default: "New Database Connection" },
            // { label: "Account", value: "account" },
            { label: "Host", value: "host" },
            { label: "User", value: "user" },
            { label: "Password", value: "password", isPassword: true },
            { label: "Database", value: "database" },
            { label: "Port", value: "port" },
            { label: "Schema", value: "schema" },
            { label: "Table", value: "table" }
        ]
    },
    {
        value: "MSSQL",
        label: "SQL Server",
        image: MSSQLIcon,
        settings: [
            { label: "Database Connection Name", value: "name", default: "New Database Connection" },
            { label: "Host", value: "host" },
            { label: "User", value: "user" },
            { label: "Password", value: "password", isPassword: true },
            { label: "Database", value: "database" },
            { label: "Port", value: "port", isOptional: true, default: 1433 },
            { label: "Schema", value: "schema" }
        ]
    }
];
export function getTableSchema(type, table) {
    const lookup = getSchemaMonitorTypeLookup(type);
    const schemaItems = table.columns
        .map((c) => {
        const type = lookup(c.type);
        if (type === undefined) {
            return undefined;
        }
        return { name: c.name, type: type };
    })
        .filter(filterUndefined);
    if (schemaItems.length === 0) {
        return undefined;
    }
    return schemaItems;
}
export function getSchemaMonitorTypeLookup(type) {
    switch (type) {
        case "BIGQUERY":
            return (t) => ({
                STRING: "TEXT",
                BOOL: "BOOLEAN",
                BOOLEAN: "BOOLEAN",
                BIGDECIMAL: "NUMERIC",
                BIGNUMERIC: "NUMERIC",
                NUMERIC: "NUMERIC",
                DECIMAL: "NUMERIC",
                FLOAT: "NUMERIC",
                FLOAT64: "NUMERIC",
                INT64: "NUMERIC",
                INTEGER: "NUMERIC",
                INT: "NUMERIC",
                SMALLINT: "NUMERIC",
                BIGINT: "NUMERIC",
                TINYINT: "NUMERIC",
                BYTEINT: "NUMERIC",
                DATE: "DATE",
                DATETIME: "DATE",
                TIMESTAMP: "DATE",
                BYTES: undefined,
                GEOGRAPHY: undefined,
                INTERVAL: undefined,
                JSON: undefined,
                ARRAY: undefined
            })[t];
        case "SNOWFLAKE":
            return (t) => {
                if (t.startsWith("VARCHAR") ||
                    t.startsWith("CHAR") ||
                    t.startsWith("CHARACTER") ||
                    t.startsWith("STRING") ||
                    t.startsWith("TEXT")) {
                    return "TEXT";
                }
                else if (t.startsWith("FIXED") ||
                    t.startsWith("NUMBER") ||
                    t.startsWith("DECIMAL") ||
                    t.startsWith("NUMERIC") ||
                    t.startsWith("FLOAT") ||
                    t.startsWith("INT") ||
                    t.endsWith("INT") ||
                    t.startsWith("DOUBLE") ||
                    t.startsWith("REAL")) {
                    return "NUMERIC";
                }
                else if (t.startsWith("TIMESTAMP") || t.startsWith("DATE")) {
                    return "DATE";
                }
                else if (t == "BOOLEAN") {
                    return "BOOLEAN";
                }
                else {
                    return undefined;
                }
            };
        case "POSTGRESQL":
        case "REDSHIFT":
            return (t) => {
                if (t.startsWith("CHAR") ||
                    t.startsWith("CHARACTER") ||
                    t.startsWith("NCHAR") ||
                    t.startsWith("BPCHAR") ||
                    t.startsWith("VARCHAR") ||
                    t.startsWith("CHARACTER VARYING") ||
                    t.startsWith("NVARCHAR") ||
                    t.startsWith("TEXT")) {
                    return "TEXT";
                }
                else if (t.startsWith("DECIMAL") ||
                    t.startsWith("NUMERIC") ||
                    t.startsWith("FLOAT") ||
                    t.startsWith("INT") ||
                    t.endsWith("INT") ||
                    t.startsWith("DOUBLE") ||
                    t.startsWith("REAL")) {
                    return "NUMERIC";
                }
                else if (t.startsWith("TIMESTAMP") || t.startsWith("DATE")) {
                    return "DATE";
                }
                else if (t.startsWith("BOOL")) {
                    return "BOOLEAN";
                }
                else {
                    return undefined;
                }
            };
        case "S3":
        case "GCS":
        case "BOOMI":
        case "AZUREBLOBSTORE":
        case "SALESFORCE":
        case "ORACLE":
        case "TERADATA":
        case "SIMBASPARK":
        case "DATABRICKS":
        default:
            return (_) => undefined;
    }
}
export const DEFAULT_DATA_SOURCE = DATA_SOURCES[6];
export const DATASET_REFRESH_RATE_OPTIONS = [
    {
        label: "Do Not Refresh",
        value: "NEVER"
    },
    {
        label: "Daily",
        value: "DAILY"
    },
    {
        label: "Weekly",
        value: "WEEKLY"
    },
    {
        label: "Monthly",
        value: "MONTHLY"
    }
];
const DEFAULT_MONITOR_SCHEMA = {
    selectedTable: undefined,
    linkTableList: [],
    linkTableOutcomes: {}
};
const initialState = {
    isShowModal: false,
    selectedMonitor: undefined,
    schemaMonitors: {}
};
function updateStateWithMonitor(state, monitor) {
    return {
        ...state,
        schemaMonitors: {
            ...state.schemaMonitors,
            [monitor.id]: {
                ...(state.schemaMonitors[monitor.id] ?? {}),
                ...monitor
            }
        }
    };
}
const schemaMonitorSlice = createSlice({
    name: "schemaMonitor",
    initialState: initialState,
    reducers: {
        updateSchemaMonitor(state, { payload }) {
            return updateStateWithMonitor(state, payload);
        },
        updateSchemaMonitors(state, { payload }) {
            return payload.reduce(updateStateWithMonitor, state);
        },
        updateKeyValue(state, { payload }) {
            const [key, value] = payload;
            return {
                ...state,
                [key]: value
            };
        }
    }
});
export const { updateSchemaMonitor, updateSchemaMonitors, updateKeyValue } = schemaMonitorSlice.actions;
/* Selectors */
export function getIsDataLink() {
    return isEnabled("link-dataset");
}
export function getUseBackendBatchProcessing(state, monitorId) {
    const monitor = getSchemaMonitor(state, monitorId);
    if (!monitor) {
        return false;
    }
    const accountId = monitor.account_id;
    const account = accountsReducer.getAccount(state, accountId);
    if (!BACKEND_PROCESSING_ACCOUNTS.includes(account?.type)) {
        return false;
    }
    if (moment(account.updated_date).isBefore(BACKEND_PROCESSING_ACCOUNT_DATE)) {
        return false;
    }
    return true;
}
export function findDataSourceDetails(type) {
    return DATA_SOURCES.find((elem) => String(elem.value).toUpperCase() === String(type).toUpperCase());
}
export function getGlobal(state) {
    return state.schemaMonitor;
}
export function getSchemaMonitors(state) {
    return getGlobal(state).schemaMonitors;
}
export function getSchemaMonitorsArr(state) {
    const selectedFolderId = foldersReducer.getSelectedFolder(state);
    return Object.values(getSchemaMonitors(state)).filter((m) => (m.folder_id ?? selectedFolderId) == selectedFolderId);
}
export function getSchemaMonitorsOptions(state) {
    return getSchemaMonitorsArr(state).map((monitor) => {
        return {
            value: monitor.id,
            label: monitor.name
        };
    });
}
export function getProcessingSchemaMonitors(state) {
    return getSchemaMonitorsArr(state).filter((monitor) => {
        return monitor.status !== "MONITORING_SCHEMA" && monitor.status !== "AUTHENTICATION_FAILURE";
    });
}
export function getSelectedSchemaMonitorId(state) {
    return getGlobal(state).selectedMonitor;
}
export function getSelectedSchemaMonitor(state) {
    const selectedMonitorId = getSelectedSchemaMonitorId(state);
    if (selectedMonitorId === undefined) {
        return undefined;
    }
    return getSchemaMonitor(state, selectedMonitorId);
}
export function getSelectedSchemaMonitorTables(state) {
    const selectedMonitorId = getSelectedSchemaMonitorId(state);
    if (selectedMonitorId === undefined) {
        return [];
    }
    return getSchemaTables(state, selectedMonitorId);
}
export function getSelectedSchemaMonitorSelectedTable(state) {
    const selectedMonitorId = getSelectedSchemaMonitorId(state);
    if (selectedMonitorId === undefined) {
        return undefined;
    }
    return getSchemaMonitorSelectedTable(state, selectedMonitorId);
}
// List of tables to be iterated across during schema Link (dataset creation)
export function getSchemaMonitorLinkTableList(state, id) {
    const schemaMonitor = getSchemaMonitor(state, id);
    return schemaMonitor?.linkTableList ?? [];
}
export function getSelectedSchemaMonitorLinkTableList(state) {
    const selectedMonitorId = getSelectedSchemaMonitorId(state);
    if (selectedMonitorId === undefined) {
        return [];
    }
    return getSchemaMonitorLinkTableList(state, selectedMonitorId);
}
export function getSchemaMonitorLinkTableTransformations(state, id) {
    const schemaMonitor = getSchemaMonitor(state, id);
    return schemaMonitor?.linkTableTransformations ?? {};
}
export function getSchemaMonitorLinkTableOutcomes(state, id) {
    const schemaMonitor = getSchemaMonitor(state, id);
    return schemaMonitor?.linkTableOutcomes ?? {};
}
export function getSelectedSchemaMonitorLinkTableOutcomes(state) {
    const selectedMonitorId = getSelectedSchemaMonitorId(state);
    if (selectedMonitorId === undefined) {
        return {};
    }
    return getSchemaMonitorLinkTableOutcomes(state, selectedMonitorId);
}
export function getSchemaMonitorLinkTableRefreshRates(state, id) {
    const schemaMonitor = getSchemaMonitor(state, id);
    return schemaMonitor?.linkTableRefreshRates ?? {};
}
export function getSelectedSchemaMonitorLinkTableRefreshRates(state) {
    const selectedMonitorId = getSelectedSchemaMonitorId(state);
    if (selectedMonitorId === undefined) {
        return {};
    }
    return getSchemaMonitorLinkTableRefreshRates(state, selectedMonitorId);
}
export function getSchemaMonitorUploadCount(state, id) {
    const schemaMonitor = getSchemaMonitor(state, id);
    return schemaMonitor?.uploadCount ?? 0;
}
export function getIsShowModal(state) {
    return getGlobal(state).isShowModal;
}
export function getSchemaMonitor(state, id) {
    return (getSchemaMonitors(state) || {})[id];
}
export function getSchemaTables(state, id) {
    const schemaMonitor = getSchemaMonitor(state, id);
    return schemaMonitor?.schema?.tables ?? [];
}
export function getSchemaMonitorTable(state, id, table) {
    const tables = getSchemaTables(state, id);
    return tables.find((t) => t.name === table);
}
export function getSchemaMonitorSelectedTable(state, id) {
    const schemaMonitor = getSchemaMonitor(state, id);
    return schemaMonitor?.selectedTable;
}
export function getTransformRecommendations(state, id, table) {
    const monitor = getSchemaMonitor(state, id);
    if (monitor === undefined) {
        return [];
    }
    const datasets = homeReducer.getDatasetsArr(state).filter((d) => (!monitor.folder_id || d.folder_id == monitor.folder_id) &&
        d.transformations.plan?.relation !== undefined &&
        d.transformations.plan.relation.type !== "DATA_SOURCE");
    const baseRelation = RelationUtils.fromDataSource({
        url: monitor.url,
        content: {
            schema_monitor_id: monitor.id,
            type: "CONNECTION_METADATA",
            provider: monitor.type,
            table: table.name,
            schema: getTableSchema(monitor.type, table)
        }
    }).build();
    const currentContext = RelationUtils.getOutputContext(baseRelation);
    return datasets
        .filter((d) => RelationUtils.getSources(RelationUtils.fromDataset(d)).some((r) => {
        const update = RelationUtils.contextFromSchemaItems(r.content.schema);
        return Object.entries(update).every(([key, output]) => currentContext[key]?.type == output.type);
    }))
        .map((d) => d.id);
}
export function getIsSafeImportMode(state, id) {
    const schemaMonitor = getSchemaMonitor(state, id);
    if (schemaMonitor === undefined) {
        return undefined;
    }
    return schemaMonitor.safeImportMode;
}
/* Actions */
export function updateMonitorKey(id, key, value) {
    return (dispatch, getState) => {
        const monitor = getSchemaMonitor(getState(), id);
        if (monitor) {
            dispatch(updateSchemaMonitor({ ...monitor, [key]: value }));
        }
    };
}
export function updateIsShowModal(isShowModal) {
    return (dispatch) => {
        dispatch(updateKeyValue(["isShowModal", isShowModal]));
        if (!isShowModal) {
            dispatch(updateSelectedMonitor(undefined));
        }
    };
}
export function updateSelectedMonitor(selectedMonitor) {
    return (dispatch) => {
        dispatch(updateKeyValue(["selectedMonitor", selectedMonitor]));
    };
}
export function deleteSchemaMonitorById(id) {
    return (dispatch, getState) => {
        const monitors = { ...getSchemaMonitors(getState()) };
        delete monitors[id];
        dispatch(updateKeyValue(["schemaMonitors", monitors]));
    };
}
export function updateSchemaSelectedTable(id, table) {
    return (dispatch) => {
        dispatch(updateMonitorKey(id, "selectedTable", table));
    };
}
export function updateSchemaLinkTableList(id, linkTableList) {
    return (dispatch) => {
        dispatch(updateMonitorKey(id, "linkTableList", linkTableList));
    };
}
export function toggleSchemaLinkTable(monitorId, table) {
    return (dispatch, getState) => {
        const linkTableList = getSchemaMonitorLinkTableList(getState(), monitorId);
        const isInTable = linkTableList?.find((t) => t?.name === table?.name);
        let retLinkTableList;
        if (isInTable) {
            retLinkTableList = linkTableList?.filter((t) => t?.name !== table?.name);
        }
        else {
            retLinkTableList = [...linkTableList, table];
        }
        dispatch(updateSchemaLinkTableList(monitorId, retLinkTableList));
    };
}
export function updateSchemaLinkTableOutcomes(id, linkTableOutcomes) {
    return (dispatch) => {
        dispatch(updateMonitorKey(id, "linkTableOutcomes", linkTableOutcomes));
    };
}
export function updateSchemaLinkTableOutcome(monitorId, table, outcome) {
    return (dispatch, getState) => {
        const linkTableOutcome = getSchemaMonitorLinkTableOutcomes(getState(), monitorId);
        const retLinkTableOutcome = {
            ...linkTableOutcome,
            [table?.name]: outcome
        };
        dispatch(updateSchemaLinkTableOutcomes(monitorId, retLinkTableOutcome));
    };
}
export function updateSelectedSchemaLinkTableOutcome(table, outcome) {
    return (dispatch, getState) => {
        const monitorId = getSelectedSchemaMonitorId(getState());
        if (monitorId !== undefined) {
            return dispatch(updateSchemaLinkTableOutcome(monitorId, table, outcome));
        }
    };
}
export function updateSchemaMonitorLinkTableTransformation(monitorId, table, transformation) {
    return (dispatch, getState) => {
        const selectedTransformations = getSchemaMonitor(getState(), monitorId)?.selectedTransformations;
        dispatch(updateMonitorKey(monitorId, "selectedTransformations", {
            ...selectedTransformations,
            [table?.name]: transformation
        }));
    };
}
export function updateSchemaLinkTableRefreshRates(id, linkTableRefreshRates) {
    return (dispatch) => {
        dispatch(updateMonitorKey(id, "linkTableRefreshRates", linkTableRefreshRates));
    };
}
export function updateSchemaLinkTableRefreshRate(monitorId, table, rate) {
    return (dispatch, getState) => {
        const linkTableRefreshRates = getSchemaMonitorLinkTableRefreshRates(getState(), monitorId);
        const retLinkTableRefreshRates = {
            ...linkTableRefreshRates,
            [table?.name]: rate
        };
        dispatch(updateSchemaLinkTableRefreshRates(monitorId, retLinkTableRefreshRates));
    };
}
export function updateSelectedSchemaLinkTableRefreshRate(table, rate) {
    return (dispatch, getState) => {
        const monitorId = getSelectedSchemaMonitorId(getState());
        if (monitorId !== undefined) {
            return dispatch(updateSchemaLinkTableRefreshRate(monitorId, table, rate));
        }
    };
}
export function updateSelectedSchemaLinkTableList(linkTableList) {
    return (dispatch, getState) => {
        const monitorId = getSelectedSchemaMonitorId(getState());
        if (monitorId !== undefined) {
            return dispatch(updateSchemaLinkTableList(monitorId, linkTableList));
        }
    };
}
export function toggleSelectedSchemaLinkTable(table) {
    return (dispatch, getState) => {
        const monitorId = getSelectedSchemaMonitorId(getState());
        if (monitorId !== undefined) {
            return dispatch(toggleSchemaLinkTable(monitorId, table));
        }
    };
}
export function toggleSafeModeImport(safeModeImport) {
    return (dispatch, getState) => {
        const monitorId = getSelectedSchemaMonitorId(getState());
        if (monitorId !== undefined) {
            return dispatch(updateMonitorKey(monitorId, "safeImportMode", safeModeImport));
        }
    };
}
export function updateUploadCount(id, count) {
    return (dispatch) => {
        dispatch(updateMonitorKey(id, "uploadCount", count));
    };
}
export function incrementUploadCount(id) {
    return (dispatch, getState) => {
        const monitor = getSchemaMonitor(getState(), id);
        if (monitor) {
            dispatch(updateMonitorKey(id, "uploadCount", (monitor.uploadCount ?? 0) + 1));
        }
    };
}
function parseSchemaMonitor(state, monitor, useDefault = false) {
    const retMonitor = { ...monitor, ...DEFAULT_MONITOR_SCHEMA };
    if (!useDefault) {
        const id = retMonitor.id;
        const selectedTable = getSchemaMonitorSelectedTable(state, id);
        retMonitor.selectedTable = selectedTable;
    }
    return retMonitor;
}
export function fetchSchemaMonitors() {
    return (dispatch, getState) => {
        const state = getState();
        if (!globalReducer.getIsSchemaMonitorLoading(state)) {
            dispatch(globalReducer.updateIsSchemaMonitorsLoading(true));
        }
        return SchemaMonitorAPI.getSchemaMonitors()
            .then((response) => {
            dispatch(globalReducer.updateIsSchemaMonitorsLoading(false));
            dispatch(updateSchemaMonitors(response.map((monitor) => parseSchemaMonitor(state, monitor, Boolean(getSchemaMonitor(state, monitor.id))))));
        })
            .catch(() => {
            dispatch(globalReducer.updateIsSchemaMonitorsLoading(false));
        });
    };
}
export function fetchSchemaMonitor(id) {
    return (dispatch, getState) => {
        return SchemaMonitorAPI.getSchemaMonitor(id).then((response) => {
            const state = getState();
            dispatch(updateSchemaMonitor(parseSchemaMonitor(state, response)));
        });
    };
}
export function pollSchemaMonitors() {
    return (dispatch, getState) => {
        return Promise.all(getProcessingSchemaMonitors(getState()).map((monitor) => dispatch(fetchSchemaMonitor(monitor.id)))).then((response) => response);
    };
}
export function openAndSelectSchema(id) {
    return (dispatch) => {
        dispatch(selectSchemaMonitor(id));
        return dispatch(updateIsShowModal(true));
    };
}
export function createSchemaMonitor(settings, folderId) {
    return async (dispatch, getState) => {
        const accountId = accountsReducer.getSelectedAccountId(getState());
        dispatch(globalReducer.updateIsSchemaMonitorsLoading(true));
        const newSchemaMonitor = await SchemaMonitorAPI.createSchemaMonitor(accountId, settings, folderId);
        if ("isOAuth" in newSchemaMonitor && "oAuthUrl" in newSchemaMonitor) {
            await dispatch(handleOAuthAuthorizationRequired(newSchemaMonitor));
            dispatch(globalReducer.updateIsSchemaMonitorsLoading(false));
            return;
        }
        try {
            const schemaMonitorId = newSchemaMonitor.id;
            await dispatch(fetchSchemaMonitor(schemaMonitorId));
            dispatch(globalReducer.updateIsSchemaMonitorsLoading(false));
            dispatch(selectSchemaMonitor(schemaMonitorId));
            return;
        }
        catch {
            if (!getIsDemoAccount()) {
                dispatch(errorsReducer.updateError("There was an error creating the Database Connection"));
            }
            dispatch(globalReducer.updateIsSchemaMonitorsLoading(false));
            return;
        }
    };
}
export function deleteSchemaMonitor(id) {
    return (dispatch) => {
        const shouldDelete = confirm("This will delete the Database Connection and all datasets created from this connection. " +
            "You will be unable to receive updates to training data created from this source or " +
            "create new training data from tables in this database.");
        if (shouldDelete) {
            dispatch(selectSchemaMonitor(undefined));
            return SchemaMonitorAPI.deleteSchemaMonitor(id).then(() => {
                dispatch(deleteSchemaMonitorById(id));
            });
        }
        else {
            return Promise.resolve();
        }
    };
}
export function refreshSchemaMonitor(id) {
    return (dispatch, getState) => {
        return SchemaMonitorAPI.patchSchemaMonitor(id, {
            status: "UPDATE_CREDENTIALS"
        }).then((response) => {
            if ("id" in response) {
                dispatch(updateSchemaMonitor(parseSchemaMonitor(getState(), response, false)));
            }
            else {
                const oAuthRequiredResponse = {
                    id: id,
                    isOAuth: true,
                    oAuthUrl: response?.oauth_begin_url,
                    schemaMonitorId: id
                };
                dispatch(handleOAuthAuthorizationRequired(oAuthRequiredResponse));
            }
        });
    };
}
export function selectSchemaMonitor(id) {
    return (dispatch) => {
        dispatch(updateSelectedMonitor(id));
    };
}
export function generateOutcomeRecommendations(id) {
    return (dispatch, getState) => {
        const sTables = getSchemaTables(getState(), id);
        const datasets = viewReducer.getDatasets(getState());
        const linkTableOutcomes = getSchemaMonitorLinkTableOutcomes(getState(), id);
        if (sTables !== undefined) {
            sTables.forEach((table) => {
                if (linkTableOutcomes?.[table?.name]?.length) {
                    return;
                }
                // TODO: Match on columns instead of just name
                const matchDataset = datasets && table.columns
                    ? datasets.find((d) => table.columns.find((c) => d.outcome !== undefined && String(d.outcome).toLowerCase() === String(c.name).toLowerCase()) !== undefined)
                    : undefined;
                if (matchDataset) {
                    dispatch(updateSchemaLinkTableOutcome(id, table, [{ label: matchDataset?.outcome, value: matchDataset?.outcome }]));
                }
                //console.log("table match outcome", table, matchDataset);
            });
        }
    };
}
export function removeOutcomeRecommendations(id) {
    return (dispatch, getState) => {
        const retId = id ?? getSelectedSchemaMonitorId(getState());
        dispatch(updateMonitorKey(retId, "linkTableOutcomes", {}));
    };
}
export function generateTransformRecommendations(id) {
    return (dispatch, getState) => {
        const retId = id ?? getSelectedSchemaMonitorId(getState());
        const monitor = getSchemaMonitor(getState(), retId);
        if (monitor === undefined) {
            return;
        }
        const sTables = getSchemaTables(getState(), retId);
        const transformations = sTables.reduce((transformations, table) => {
            if (transformations) {
                transformations[table.name] = getTransformRecommendations(getState(), id, table)[0];
            }
            return transformations;
        }, {});
        dispatch(updateMonitorKey(retId, "selectedTransformations", transformations));
    };
}
export function resetTransformRecommendations(id) {
    return (dispatch) => {
        dispatch(updateMonitorKey(id, "selectedTransformations", {}));
    };
}
export function selectTableInSelectedMonitor(table) {
    return (dispatch, getState) => {
        const selectedMonitorId = getSelectedSchemaMonitorId(getState());
        if (selectedMonitorId === undefined) {
            return;
        }
        return dispatch(selectTable(selectedMonitorId, table));
    };
}
export function selectTable(id, table) {
    return (dispatch) => {
        dispatch(updateSchemaSelectedTable(id, table));
    };
}
export function linkSchemaMonitorTable(id, table, outcome, monitorOverride, folderIdOverride, useBackendProcessing = false) {
    return (dispatch, getState) => {
        const state = getState();
        const monitor = monitorOverride ?? getSchemaMonitor(getState(), id);
        if (monitor === undefined) {
            return Promise.resolve(undefined);
        }
        const monitorTable = table ?? getSchemaMonitorSelectedTable(state, id);
        const accountId = monitor.account_id;
        const monitorType = monitor.type;
        const folderId = folderIdOverride ?? foldersReducer.getSelectedFolder(state);
        const name = `${monitorTable.name} (${monitor.name})`;
        const transformTarget = monitor?.selectedTransformations?.[monitorTable.name];
        const source = {
            url: monitor.url,
            content: {
                schema_monitor_id: monitor.id,
                type: "CONNECTION_METADATA",
                provider: monitor.type,
                table: table.name,
                schema: getTableSchema(monitor.type, table)
            }
        };
        const settings = {
            name: name,
            folder_id: folderId,
            account_id: accountId,
            target: outcome,
            labels: outcome ? table?.columns?.map((c) => (typeof c === "string" ? c : c?.name)) : undefined,
            source_content: source.content
        };
        if (monitor.safeImportMode) {
            settings.options = {
                target_model_params: {
                    data: {
                        summary_detail: "low"
                    }
                }
            };
        }
        if (transformTarget !== undefined) {
            const targetDataset = homeReducer.getDataset(getState(), transformTarget);
            if (targetDataset !== undefined) {
                const updates = RelationUtils.toComposableList(RelationUtils.fromDataset(targetDataset));
                const builder = RelationUtils.fromDataSource(source);
                let updatedBuilder = undefined;
                for (const update of updates) {
                    updatedBuilder = (builder ?? updatedBuilder).cherryPick(update);
                }
                if (updatedBuilder !== undefined) {
                    const relation = updatedBuilder.build();
                    settings.source_content = source.content;
                    settings.transformations = {
                        plan: { relation: relation }
                    };
                    if (outcome) {
                        const context = RelationUtils.getOutputContext(relation);
                        if (context[outcome] !== undefined) {
                            settings.labels = getSchemaColumns(context);
                        }
                        else {
                            delete settings.target;
                            delete settings.labels;
                        }
                    }
                }
            }
        }
        if (useBackendProcessing) {
            settings.skip_task_creation = true;
            delete settings.target;
        }
        return SchemaMonitorAPI.linkSchemaMonitor(id, monitorType, monitorTable.name, settings).then((response) => {
            dispatch(homeReducer.updateDataset(response.id, homeReducer.transformDataset(response)));
            return homeReducer.getDataset(getState(), response.id);
        });
    };
}
export function linkSchemaMonitorTables(id, linkTableListOverride, linkTableOutcomesOverride, linkTableRefreshRatesOverride, useBackendProcessingOverride) {
    return async (dispatch, getState) => {
        const monitor = getSchemaMonitor(getState(), id);
        if (monitor === undefined) {
            return Promise.resolve([]);
        }
        const accountId = monitor.account_id;
        const folderId = foldersReducer.getSelectedFolder(getState());
        const linkTableList = linkTableListOverride ?? getSchemaMonitorLinkTableList(getState(), id);
        const linkTableOutcomes = linkTableOutcomesOverride ?? getSchemaMonitorLinkTableOutcomes(getState(), id);
        const linkTableRefreshRates = linkTableRefreshRatesOverride ?? getSchemaMonitorLinkTableRefreshRates(getState(), id);
        const MAX_CALLS = 10;
        const errors = [];
        // TODO: fix backend processing on the armen side for local workflows
        const useBackendProcessing = false; //useBackendProcessingOverride ?? getUseBackendBatchProcessing(getState(), id);
        dispatch(updateUploadCount(id, 0));
        const nDatasets = [];
        const linkSchemaMonitorTablesIterator = (retLinkTableList) => {
            if (!retLinkTableList?.length) {
                if (useBackendProcessing) {
                    const datasetsArr = nDatasets.map((d) => {
                        const retObj = {
                            id: d?.datasetId
                        };
                        if (d?.outcome) {
                            retObj.target = d?.outcome;
                        }
                        return retObj;
                    });
                    const accountClient = AibleClientFactory.getInstance().getAccountClient();
                    accountClient
                        .getCustomerClientFactory(accountId)
                        .then((factory) => factory.getDatasetClient().initiateDatasetProcessing(id, datasetsArr))
                        .then((r) => {
                        console.log(r);
                    })
                        .catch((e) => {
                        toast(`There was an error initializing the analysis of your datasets. ${e}`, {
                            type: toast.TYPE.ERROR,
                            position: toast.POSITION.TOP_CENTER,
                            autoClose: 7500
                        });
                    });
                }
                if (errors?.length) {
                    toast(`There was an error importing ${errors.map((e) => e.table).join(", ")}`, {
                        type: toast.TYPE.ERROR,
                        position: toast.POSITION.TOP_CENTER,
                        autoClose: 7500
                    });
                }
                else {
                    dispatch(updateUploadCount(id, 0));
                    dispatch(updateSchemaLinkTableList(id, []));
                }
                return Promise.resolve();
            }
            const currTables = retLinkTableList?.slice(0, MAX_CALLS);
            const remaingTables = retLinkTableList?.slice(MAX_CALLS);
            return Promise.all(currTables.map((table) => {
                const currOutcome = linkTableOutcomes?.[table?.name]?.[0]?.value;
                const currRefreshRate = linkTableRefreshRates?.[table?.name];
                return dispatch(linkSchemaMonitorTable(id, table, currOutcome, monitor, folderId, useBackendProcessing))
                    .then((nData) => {
                    const datasetId = nData?.id;
                    if (datasetId) {
                        nDatasets.push({
                            table: table,
                            datasetId: datasetId,
                            outcome: currOutcome
                        });
                    }
                    if (datasetId && isEnabled("schema-monitor-autorefresh")) {
                        dispatch(setupReducer.updateDatasetRefreshSchedule(`data_${datasetId}`, currRefreshRate));
                    }
                    dispatch(incrementUploadCount(id));
                    return nData;
                })
                    .catch((e) => {
                    console.log(e);
                    errors.push({
                        table: table?.name,
                        error: e
                    });
                });
            }))
                .then(() => {
                return linkSchemaMonitorTablesIterator(remaingTables);
            })
                .catch(() => {
                return linkSchemaMonitorTablesIterator(remaingTables);
            });
        };
        // Awaiting the iterator and then getting array of nDatasets Ids to be resolved for selecting a dataset for the link to databse in agent setup flow.
        await linkSchemaMonitorTablesIterator(linkTableList);
        const newDatasets = nDatasets.map((nData) => nData?.datasetId);
        return Promise.resolve(newDatasets);
    };
}
export function linkSelectedSchemaMonitorTables() {
    return async (dispatch, getState) => {
        const selectedMonitorId = getSelectedSchemaMonitorId(getState());
        if (selectedMonitorId === undefined) {
            return Promise.resolve([]);
        }
        return await dispatch(linkSchemaMonitorTables(selectedMonitorId));
    };
}
export function patchSchemaMonitor(id, settings) {
    return (dispatch) => {
        return SchemaMonitorAPI.patchSchemaMonitor(id, settings).then(() => {
            return dispatch(fetchSchemaMonitor(id));
        });
    };
}
export function patchSelectedSchemaMonitor(settings) {
    return (dispatch, getState) => {
        const state = getState();
        const selectedMonitorId = getSelectedSchemaMonitorId(state);
        if (selectedMonitorId === undefined) {
            return Promise.resolve();
        }
        return dispatch(patchSchemaMonitor(selectedMonitorId, settings));
    };
}
export function updateSelectedSchemaMonitorAuth(settings, forceRefresh = false) {
    return (dispatch, getState) => {
        const state = getState();
        const selectedMonitorId = getSelectedSchemaMonitorId(state);
        if (selectedMonitorId === undefined) {
            return Promise.resolve();
        }
        return dispatch(updateSchemaMonitorAuth(selectedMonitorId, settings, forceRefresh));
    };
}
export function updateSchemaMonitorAuth(id, settings, forceRefresh = false) {
    return async (dispatch, getState) => {
        const monitor = getSchemaMonitor(getState(), id);
        if (monitor === undefined) {
            return Promise.resolve();
        }
        settings = { ...settings, type: monitor.type };
        if (!globalReducer.getIsSchemaMonitorLoading(getState())) {
            dispatch(globalReducer.updateIsSchemaMonitorsLoading(true));
        }
        const update = await SchemaMonitorAPI.updateSchemaMonitorAuth(monitor, settings, forceRefresh);
        if ("isOAuth" in update && "oAuthUrl" in update) {
            dispatch(globalReducer.updateIsSchemaMonitorsLoading(false));
            await dispatch(handleOAuthAuthorizationRequired(update));
            return;
        }
        await dispatch(fetchSchemaMonitor(id));
        dispatch(globalReducer.updateIsSchemaMonitorsLoading(false));
        return;
    };
}
export function fetchSchemaMonitorCredentials(id) {
    return (dispatch, getState) => {
        const retId = id ?? getSelectedSchemaMonitorId(getState());
        if (retId === undefined) {
            return Promise.resolve(undefined);
        }
        const monitor = getSchemaMonitor(getState(), retId);
        if (monitor === undefined) {
            return Promise.resolve(undefined);
        }
        return AibleClientFactory.getInstance()
            .getAccountClient()
            .getCustomerClientFactory(monitor.account_id)
            .then((factory) => factory.getSchemaMonitorClient().getCredentials(monitor.id))
            .then((cResponse) => {
            if ("credentials" in cResponse) {
                const isOAuth = cResponse?.type === "incomplete-authorization" && cResponse?.oauth_begin_url !== undefined;
                const credentialsSettings = {
                    ...cResponse.credentials,
                    oAuthLogin: isOAuth
                };
                if (isOAuth) {
                    const oAuthRequiredResponse = {
                        id: monitor.id,
                        isOAuth: true,
                        oAuthUrl: cResponse?.oauth_begin_url,
                        schemaMonitorId: monitor.id
                    };
                    // Should trigger the oAuth UI
                    dispatch(handleOAuthAuthorizationRequired(oAuthRequiredResponse));
                }
                return credentialsSettings;
            }
            return cResponse;
        });
    };
}
export function filterSchemaMonitorSettings(settings, source) {
    const cleanSettings = {};
    const INVALID_KEYS = settings.type == "incomplete-authorization"
        ? ["credentials", "expected_oauth2_redirect_url", "message", "oauth_begin_url", "oAuthLogin"]
        : [];
    // Now constructing new object of valid parameters, ignoring settings that are in INVALID_KEYS and not adding
    // in the construction if they should not be sent as null or ignored by setting.
    for (const key in settings) {
        if (!INVALID_KEYS.includes(key)) {
            if (source) {
                const foundSetting = source?.settings.find((s) => s?.value == key);
                const dontSendSetting = foundSetting?.dontSend || (foundSetting?.hideFunc && foundSetting?.hideFunc(settings) === true);
                const dontSendNullSetting = foundSetting?.dontSendNull && (settings[key] === undefined || settings[key] === null || settings[key] === "");
                const shouldIgnoreSetting = dontSendSetting || dontSendNullSetting;
                if (shouldIgnoreSetting) {
                    continue;
                }
            }
            cleanSettings[key] = settings[key];
        }
    }
    return cleanSettings;
}
// This handles getting a OAuth response and updates the Schema monitor with the url for OAuth and selects the newly
// created schema monitor so we can render the authorize button.
export function handleOAuthAuthorizationRequired(oAuthResponse) {
    return async (dispatch, getState) => {
        await dispatch(fetchSchemaMonitor(oAuthResponse.id));
        dispatch(updateMonitorKey(oAuthResponse.id, "oAuthUrl", oAuthResponse.oAuthUrl));
        dispatch(updateMonitorKey(oAuthResponse.id, "requiresOAuth", true));
        dispatch(selectSchemaMonitor(oAuthResponse.id));
        return;
    };
}
// This opens the OAuth window from button click to prevent the popup from being blocked and then passes the window
// into another function to asynchronously check for window close and handle fetching the Schema Monitor on complete.
export function handleOAuthAuthorization(id, accountId, oAuthUrl) {
    return (dispatch, getState) => {
        const width = 500;
        const height = 600;
        const left = window.screenX + (window.innerWidth - width) / 2;
        const top = window.screenY + (window.innerHeight - height) / 2;
        const oAuthWindow = window.open(oAuthUrl, "popup", `popup=true,width=${width},height=${height},left=${left},top=${top}`);
        // const redirectUrl = new URL(oAuthUrl).searchParams.get("redirect_uri");
        if (oAuthUrl) {
            dispatch(updateMonitorKey(id, "isAttemptingOAuth", true));
            dispatch(handleOAuthPopup(id, accountId, oAuthUrl, oAuthWindow));
        }
        else {
            console.log("Missing redirect URL params");
        }
        return;
    };
}
// Passes the OAuth Window into the handler and waits for the handling to complete before refreshing the schemaMonitor.
export function handleOAuthPopup(id, accountId, oAuthUrl, oAuthWindow) {
    return async (dispatch, getState) => {
        //  Now passing account so we can filter event message handling to ones from the cloud url
        const account = accountsReducer.getAccount(getState(), accountId);
        const oAuthResult = await SchemaMonitorAPI.handleOAuth(id, oAuthUrl, oAuthWindow, account);
        if (oAuthResult?.success) {
            dispatch(globalReducer.updateIsSchemaMonitorsLoading(true));
            await dispatch(refreshSchemaMonitor(id));
            dispatch(updateMonitorKey(id, "isAttemptingOAuth", false));
            dispatch(updateMonitorKey(id, "requiresOAuth", false));
            dispatch(globalReducer.updateIsSchemaMonitorsLoading(false));
        }
        else {
            if (oAuthResult?.oAuthResponse !== undefined) {
                await dispatch(handleOAuthAuthorizationRequired(oAuthResult.oAuthResponse));
            }
            dispatch(updateMonitorKey(id, "isAttemptingOAuth", false));
            return;
        }
    };
}
export default schemaMonitorSlice.reducer;
